import { GestTokenGenerate, GetLockerDetailsNearToUser } from '../api/api';
import { LOCAL_STORAGE } from '../assets/constants/BrowserStorageKeys';
import { storeSecureItemInSpecifiedStorage } from './BrowserStorageAccessMiddleware';

const DEFAULT_LOCKER_DISTANCE = 1000000;

const updateLoadingMessage = (onLoadingMessage, message) => {
  if (typeof onLoadingMessage === 'function') {
    onLoadingMessage(message);
  }
};

const createNearbyLockerError = (code, message, cause = null) => {
  const error = new Error(message);
  error.code = code;
  error.cause = cause;
  return error;
};

export const getCurrentBrowserLocation = (
  geolocation = typeof window !== 'undefined' ? window.navigator?.geolocation : null,
  options = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  }
) =>
  new Promise((resolve, reject) => {
    if (!geolocation) {
      reject(
        createNearbyLockerError(
          'GEOLOCATION_NOT_SUPPORTED',
          'Geolocation is not supported in this browser.'
        )
      );
      return;
    }

    geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        let errorCode = 'LOCATION_FETCH_FAILED';

        if (error?.code === 1) {
          errorCode = 'LOCATION_PERMISSION_DENIED';
        } else if (error?.code === 2) {
          errorCode = 'LOCATION_UNAVAILABLE';
        } else if (error?.code === 3) {
          errorCode = 'LOCATION_TIMEOUT';
        }

        reject(
          createNearbyLockerError(
            errorCode,
            error?.message || 'Unable to fetch current location.',
            error
          )
        );
      },
      options
    );
  });

export const fetchNearbyLockersForLocation = async ({
  userLocation,
  onLoadingMessage,
  distance = DEFAULT_LOCKER_DISTANCE,
  shouldStoreLocation = true,
} = {}) => {
  if (!userLocation?.lat || !userLocation?.lng) {
    throw createNearbyLockerError(
      'VALID_LOCATION_REQUIRED',
      'Valid latitude and longitude are required.'
    );
  }

  updateLoadingMessage(onLoadingMessage, 'please wait.....');

  const responseGenerateToken = await GestTokenGenerate();
  const guestToken = responseGenerateToken?.data?.token;

  if (guestToken) {
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, 'guess_token', guestToken);
  }

  if (shouldStoreLocation) {
    storeSecureItemInSpecifiedStorage(
      LOCAL_STORAGE,
      'userlocationDetails',
      userLocation
    );
  }

  if (responseGenerateToken?.status !== 'success') {
    throw createNearbyLockerError(
      'GUEST_TOKEN_FAILED',
      'User are not able to authemitcted as gest user'
    );
  }

  updateLoadingMessage(onLoadingMessage, 'Getting lockers near you...');

  const response = await GetLockerDetailsNearToUser({
    distance,
    location: userLocation,
    token: guestToken,
  });

  if (response?.data?.status !== 'success') {
    throw createNearbyLockerError(
      'LOCKER_FETCH_FAILED',
      'Failed to fetch lockers data'
    );
  }

  updateLoadingMessage(onLoadingMessage, 'Opening locker map...');

  return {
    lockersData: response?.data?.data || [],
    userLocation,
    token: guestToken,
  };
};

export const getNearbyLockerErrorMessage = (error) => {
  switch (error?.code) {
    case 'GEOLOCATION_NOT_SUPPORTED':
      return 'location did not permit please allow it';
    case 'LOCATION_PERMISSION_DENIED':
      return 'Location permission denied. Please allow location access.';
    case 'LOCATION_TIMEOUT':
      return 'Location request timed out. Please try again.';
    case 'LOCATION_UNAVAILABLE':
      return 'Unable to get your location right now.';
    case 'GUEST_TOKEN_FAILED':
      return 'User are not able to authemitcted as gest user';
    case 'LOCKER_FETCH_FAILED':
      return 'Failed to fetch lockers data';
    default:
      return 'Something went wrong while finding lockers';
  }
};
